/**
 * Branding section of the Pass Designer: colours (background/foreground/label) +
 * logoText. Controlled — the parent owns the working PassDesign and applies the
 * emitted DeepPartial patch via mergePassDesign semantics. Server-returned
 * PassDesignError[] are routed to their slot by path: 'error' renders red and
 * blocks save (upstream), 'warn' (low contrast) renders amber and never blocks.
 * The client does NOT re-run validation — the PUT response is the source of truth.
 *
 * Colour picking (UX4 item 1): a curated SWATCH GRID + a native
 * `<input type="color">` ("Egyéni szín") — no free-text rgb()/hex entry
 * anywhere. Every pick derives `colors.foreground`/`colors.label`
 * automatically via `autoContrastFor` (WCAG-style relative luminance), so the
 * low-contrast WARN below is no longer reachable from this UI — the plumbing
 * stays wired only so a legacy stored design that predates this UI can still
 * surface its server-returned warning.
 */

import type { DeepPartial, PassDesign, PassDesignError } from '@insurwiz/wallet-core';

import { autoContrastFor, hexToRgbString, normalizeToHex } from '../../lib/colorPick';
import { TRANSIT_TYPE_OPTIONS } from '../../lib/fieldLabels';

export interface ColorsEditorProps {
  design: PassDesign;
  errors: PassDesignError[];
  onChange: (patch: DeepPartial<PassDesign>) => void;
}

interface Swatch {
  name: string;
  hex: string;
}

// Insurwiz navy first (the brand default), then a curated
// blues/teal/green/red/orange/purple/graphite/black/white/gold/pink spread.
const SWATCHES: readonly Swatch[] = [
  { name: 'Insurwiz kék', hex: '#003366' },
  { name: 'Kék', hex: '#1d4ed8' },
  { name: 'Azúr', hex: '#0e7490' },
  { name: 'Türkiz', hex: '#0f766e' },
  { name: 'Zöld', hex: '#15803d' },
  { name: 'Piros', hex: '#b91c1c' },
  { name: 'Narancs', hex: '#c2410c' },
  { name: 'Lila', hex: '#7c3aed' },
  { name: 'Grafit', hex: '#334155' },
  { name: 'Fekete', hex: '#111827' },
  { name: 'Fehér', hex: '#ffffff' },
  { name: 'Arany', hex: '#f59e0b' },
  { name: 'Rózsaszín', hex: '#db2777' },
  { name: 'Szürke', hex: '#64748b' },
];

function notesFor(errors: PassDesignError[], path: string): PassDesignError[] {
  return errors.filter((e) => e.path === path);
}

function ErrorNotes({ errors }: { errors: PassDesignError[] }): JSX.Element | null {
  if (errors.length === 0) return null;
  return (
    <>
      {errors.map((e, i) => (
        <p
          key={`${e.path}-${String(i)}`}
          className={`mt-1 text-xs ${e.severity === 'error' ? 'text-red-600' : 'text-amber-700'}`}
        >
          {e.message}
        </p>
      ))}
    </>
  );
}

export function ColorsEditor({ design, errors, onChange }: ColorsEditorProps): JSX.Element {
  const colors = design.colors ?? {};
  // Legacy designs may store '#hhhhhh' (validatePassDesign accepts both
  // shapes) — normalizeToHex parses either, and returns null for anything
  // else. null means NO swatch may show selected: a fallback hex here would
  // falsely ring the navy swatch for every unrecognized stored colour. The
  // native input still needs a well-formed value, so IT falls back to navy.
  const parsedHex = normalizeToHex(colors.background ?? 'rgb(0, 51, 102)');

  // Every pick (swatch OR the native colour input) goes through here — always
  // derives a readable foreground/label so the low-contrast WARN below can
  // never be produced from this UI again.
  function pickBackground(hex: string): void {
    const background = hexToRgbString(hex);
    onChange({ colors: { background, ...autoContrastFor(background) } });
  }

  return (
    <section className="card p-4 space-y-3">
      <h3 className="text-sm font-semibold text-slate-800">Megjelenés</h3>

      <div className="space-y-1">
        <label className="label" htmlFor="design-logoText">
          Logó szöveg
        </label>
        <input
          id="design-logoText"
          aria-label="Logó szöveg"
          className="input"
          type="text"
          value={design.logoText ?? ''}
          onChange={(e) => onChange({ logoText: e.target.value })}
        />
      </div>

      <div className="space-y-2">
        <span className="label">Háttérszín</span>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Szín választása">
          {SWATCHES.map((s) => {
            const isSelected = parsedHex !== null && s.hex.toLowerCase() === parsedHex;
            return (
              <button
                key={s.hex}
                type="button"
                aria-label={`Szín: ${s.name}`}
                aria-pressed={isSelected}
                title={s.name}
                onClick={() => pickBackground(s.hex)}
                // A baseline `ring-black/10` keeps every swatch (notably the
                // white one) visually defined against the card background;
                // selection upgrades to a bold `ring-brand-700`. Both are
                // `ring-*` utilities (a `style` box-shadow here would silently
                // WIN over them and hide the ring entirely) — background is
                // the only inline style this button needs.
                className={`h-8 w-8 shrink-0 rounded-full ring-offset-2 transition ${
                  isSelected
                    ? 'ring-2 ring-brand-700'
                    : 'ring-1 ring-black/10 hover:ring-2 hover:ring-slate-300'
                }`}
                style={{ background: s.hex }}
              />
            );
          })}
          {/* Native colour input ("Egyéni szín") — a visual pipette fallback, no
              codes. The input itself carries the aria-label/value; the visible
              chip is just its native swatch rendering. */}
          <input
            type="color"
            aria-label="Egyéni szín"
            title="Egyéni szín"
            value={parsedHex ?? '#003366'}
            onChange={(e) => pickBackground(e.target.value)}
            className="h-8 w-8 shrink-0 cursor-pointer rounded-full border-0 bg-transparent p-0 ring-1 ring-slate-300"
          />
        </div>
        <ErrorNotes errors={notesFor(errors, 'colors.background')} />
        <ErrorNotes errors={notesFor(errors, 'colors.foreground')} />
        <ErrorNotes errors={notesFor(errors, 'colors.label')} />
      </div>

      {design.style === 'boardingPass' ? (
        <label className="flex flex-col gap-0.5">
          <span className="label">Közlekedési mód</span>
          <select
            aria-label="Közlekedési mód"
            className="input"
            value={design.transitType ?? 'PKTransitTypeAir'}
            onChange={(e) => onChange({ transitType: e.target.value as PassDesign['transitType'] })}
          >
            {TRANSIT_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      ) : null}
    </section>
  );
}
